import { Component, input, OnInit, ChangeDetectionStrategy, signal, inject, DestroyRef } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { AvatarModule } from 'primeng/avatar';
import { TranslateModule } from '@ngx-translate/core';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import type { Client } from '@fitness-force/models';
import { ClientService } from '@fitness-force/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'freeze-client-info',
  standalone: true,
  imports: [
    CommonModule,
    AvatarModule,
    TranslateModule,
    ShortNamePipe,
    TitleCasePipe
  ],
  templateUrl: './freeze-client-info.component.html',
  styleUrls: ['./freeze-client-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FreezeClientInfoComponent implements OnInit {
  // Inputs
  readonly client = input<Client | null>(null);
  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  
  // Services
  private readonly clientService = inject(ClientService);
  private readonly destroyRef = inject(DestroyRef);
  
  // Signals
  readonly loadedClient = signal<Client | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  
  ngOnInit(): void {
    // If client is not provided but clientId is, fetch client from API
    if (!this.client() || Object.keys(this.client() || {}).length === 0 && this.clientId() > 0) {
      this.loadClientById();
    }
  }
  
  /**
   * Load client by ID from API
   */
  private loadClientById(): void {
    this.isLoading.set(true);
    this.error.set(null);
    
    this.clientService.getClientById(this.clientId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if(response){
            response.clt_image = response.image || '';
            response.clt_full_name = response.full_name || '';
            response.clt_id = response.id || null;
            response.clt_status = response.client_status;
            this.loadedClient.set(response);
            this.isLoading.set(false);
          } else {
            this.error.set('Failed to load client information');
            this.isLoading.set(false);
          }
        },
        error: (err) => {
          console.error('Error loading client:', err);
          this.error.set('Failed to load client information');
          this.isLoading.set(false);
        }
      });
  }
  
  /**
   * Get the current client - either from input or loaded from API
   */
  get currentClient(): Client | null {
    return Object.keys(this.client() || {}).length > 0 ? this.client() : this.loadedClient();
  }
  
  /**
   * Get 2-letter name for avatar
   */
  client2LetterName(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return parts[0].charAt(0) + parts[1].charAt(0);
    }
    return name.substring(0, 2);
  }
  
  /**
   * Get client status HTML
   */
  clientStatus(client: Client | null): string {
    if (client && (client.clt_status || (client as any)?.client_status))
      return this.clientService.getClientStatusHtml(
        (client.clt_status || '').toString()
      );
    else return '';
  }
}
